import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup.js";
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useAppSelector } from "@store/hooks";
import {
  selectCurrentUserTier,
  selectCurrentUserTierSource,
} from "@store/selectors";
import { selectCurrentUserOrgs } from "@store/slices/orgsSlice";
import { emailSchema } from "@shared/utils/yupSchema";
import { updateEmail } from "@services/supabaseAPI";
import { requireStepUp } from "@shared/utils/stepUpManager";
import { useNotification } from "@shared/contexts/NotificationContext";

// External contact form for upgrade requests (Phase 2: manual billing).
// Phase 8 swaps this for a self-serve Stripe Payment Link.
const UPGRADE_CONTACT_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLScTkVLiXWu5nuEhLwk8OshieMoF6SVq7yR2vjrSmGP3yqp8iw/viewform";

interface EmailForm {
  email: string;
}

const formatTier = (tier: string) =>
  tier.charAt(0).toUpperCase() + tier.slice(1).replace("_", " ");

const formatTierSource = (source: string) => {
  switch (source) {
    case "subscription":
      return "Personal subscription";
    case "platform":
      return "Assigned by platform";
    default:
      return "Free tier";
  }
};

export const AccountPanel = () => {
  const { userInfo } = useAppSelector((state) => state.auth);
  const tier = useAppSelector(selectCurrentUserTier);
  const tierSource = useAppSelector(selectCurrentUserTierSource);
  const userOrgs = useAppSelector(selectCurrentUserOrgs);
  const { showSuccess, showError } = useNotification();

  // Two-step UX: button → step-up → reveal input → submit → collapse.
  // The collapse on success is the success indicator; we don't rely
  // on the toast or form reset rendering visibly through the
  // Supabase USER_UPDATED re-render cascade.
  const [editing, setEditing] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EmailForm>({
    resolver: yupResolver(emailSchema),
    defaultValues: { email: "" },
  });

  const showUpgradeCta = tier === "annotator";

  const beginEdit = async () => {
    // Pre-flight MFA so an AAL1 session can't re-point the account
    // without verifying. Supabase's "Secure email change" setting
    // double-confirms via both the old and new inbox; this gate
    // adds a session-level proof on top.
    if (!(await requireStepUp())) return;
    setEditing(true);
  };

  const onSubmit = async (data: EmailForm) => {
    const result = await updateEmail(data.email);
    if (!result.success) {
      showError(result.error || "Could not update email");
      return;
    }
    showSuccess("Check your inbox to confirm the new address");
    reset({ email: "" });
    setEditing(false);
  };

  const cancelEdit = () => {
    reset({ email: "" });
    setEditing(false);
  };

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Account
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Your sign-in identity and current plan.
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 3,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="overline" color="text.secondary">
            Email
          </Typography>
          <Typography sx={{ mb: 2 }}>
            {userInfo?.email ?? "—"}
          </Typography>
          {editing ? (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  {...register("email")}
                  label="New email"
                  size="small"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  autoFocus
                />
                <Button
                  type="submit"
                  variant="outlined"
                  color="inherit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving…" : "Save"}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  color="cancel"
                  disabled={isSubmitting}
                  onClick={cancelEdit}
                >
                  Cancel
                </Button>
              </Stack>
            </form>
          ) : (
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              onClick={beginEdit}
            >
              Change email
            </Button>
          )}
        </Box>

        <Box>
          <Typography variant="overline" color="text.secondary">
            Plan
          </Typography>
          <Typography>{formatTier(tier)}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            {formatTierSource(tierSource)}
          </Typography>
          {showUpgradeCta && (
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              href={UPGRADE_CONTACT_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textTransform: "none" }}
            >
              Request upgrade
            </Button>
          )}
          {userOrgs.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="overline" color="text.secondary">
                Organizations
              </Typography>
              <Stack
                direction="row"
                flexWrap="wrap"
                sx={{ gap: 1, mt: 0.5 }}
              >
                {userOrgs.map((org) => (
                  <Chip
                    key={org.org_id}
                    size="small"
                    clickable
                    component={RouterLink}
                    to={`/orgs/${org.slug}/settings`}
                    label={`${org.name} · ${
                      org.role === "org_admin" ? "Admin" : "Member"
                    }`}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default AccountPanel;
